import React, { useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import classNames from 'classnames';
import { environmentURLs } from '../common/CaptureEnvironment';
import {
    TableHeader,
    TablePagination,
    TableContent,
    TableContainer,
    TableColumnDndContainer,
    classNamePrefix,
    usePreventTableScrollingRef,
    TextRenderer,
    CELL_CLASSES,
    CELL_RENDERERS,
    COLUMN_TYPES,
    preventDefaultBehavior,
    focusLastCell,
    DEFAULT_ROWS_PER_PAGE_OPTIONS,
    TABLE_LAYOUT_OPTIONS,
    IconFileDownload,
    Loader
} from 'cdk-radial';


const componentMainClass = `${classNamePrefix}-table`;
const agGridThemeClass = 'ag-theme-balham';
const purchaseTableClass = 'purchaseTable';
const tableClass = classNames(componentMainClass, agGridThemeClass, purchaseTableClass);
const LOADER_STYLE = {
    stroke: '#006CFA',
    "margin-top" : '200px ',
    display: 'flex'
  };;


const customAppNameRenderer = props => {
    const { app_details = {} } = props.data;
    return (<div className="purchase-history-app-block">
        <span className="purchase-history-app-logo-container">
            <img
                src={app_details.app_image}
                alt="App Logo"
                className="purchase-history-app-logo"
            ></img>
        </span>
        <span className="purchase-history-app-name">
            <a className='purchase-history-app-link' href={environmentURLs.REACT_APP_MARKETPLACE_URL + 'solutions/' + app_details.app_id} target="_blank" rel="noreferrer">
                {app_details.app_name}
            </a>
        </span>
    </div>)
}

const columns = [
    {
        cellRendererFramework: customAppNameRenderer,
        field: 'app_details',
        headerName: 'App Name',
        headerTooltip: 'App Name',
        minWidth: 180,
        width: 250,
        wrapText: true
    },
    {
        cellClass: [CELL_CLASSES.ALIGN_CELL_RIGHT, CELL_CLASSES.NUMERIC_CELL],
        cellRenderer: CELL_RENDERERS.TEXT,
        field: 'amount',
        headerName: 'Amount',
        headerTooltip: 'Amount',
        minWidth: 100,
        width: 150,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        type: COLUMN_TYPES.NUMERIC_COLUMN
      },
      {
        cellRenderer: CELL_RENDERERS.TEXT,
        field: 'created',
        headerName: 'Initiated On',
        headerTooltip: 'Initiated On',
        minWidth: 150,
        width: 180,
        sortable: true
      },
      {
        cellRenderer: CELL_RENDERERS.TEXT,
        field: 'location',
        headerName: 'Initiated On',
        headerTooltip: 'Initiated On',
        minWidth: 150,
        width: 180,
        sortable: true
      }
]






export const RefundReport = ({ solutionSubscriptionInfo, dealerRefundHistoryData, dealerRefundHistoryDataLoaded, errorInGettingDealerRefundeHistroyData, entityIdLocationMap }) => {
    const [gridApi, setGridApi] = useState(null);
    const [currentPage, setCurrentPage] = useState();
    const [totalPages, setTotalPages] = useState();
    const [totalRows, setTotalRows] = useState();
    const [showPagination, setShowPagination] = useState(true);
    const [searchInputValue, setSearchInputValue] = useState('');
    const isAgGridReady = !!gridApi;
    const hasNoRows = totalRows === 0;
    const defaultPaginationPageSize = DEFAULT_ROWS_PER_PAGE_OPTIONS[0];
    const rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS;
 
    let updatedDealerPurchaseHistoryData = [];
    

    const tableContentRef = usePreventTableScrollingRef();
    const frameworkComponentsExtended = {
        textRenderer: TextRenderer
    };

    const onSearchInputChange = newValue => {
        setSearchInputValue(newValue);
    };

    const handleGridReady = gridParams => {
        setGridApi(gridParams.api);
        setCurrentPage(gridParams.api.paginationGetCurrentPage() + 1);
        setTotalPages(gridParams.api.paginationGetTotalPages());
        setTotalRows(gridParams.api.getDisplayedRowCount());
        gridParams.api.sizeColumnsToFit();
    };


    const handlePaginationChanged = () => {
        if (!isAgGridReady) return;
        setCurrentPage(gridApi.paginationGetCurrentPage() + 1);
        setTotalPages(gridApi.paginationGetTotalPages());
        setTotalRows(gridApi.getDisplayedRowCount());
    };

    const paginationPageSize = () => {
        if (isAgGridReady) {
            return gridApi.paginationGetPageSize();
        }

        if (defaultPaginationPageSize.value === DEFAULT_ROWS_PER_PAGE_OPTIONS[0].value) {
            return rowsPerPageOptions[0].value;
        }

        if (rowsPerPageOptions.map(rowPerPageOption => rowPerPageOption === defaultPaginationPageSize.value)) {
            return defaultPaginationPageSize.value;
        }
    };

    const handleTabbingFromPagination = event => {
        const hasRowsToDisplay = gridApi.rowModel.rowsToDisplay.length > 0;
        if (hasRowsToDisplay) {
            preventDefaultBehavior(event);
            focusLastCell(gridApi);
        }
    };

    const handleDownloadClick = () => {
        gridApi.exportDataAsCsv();
    };

    const handlePrintClick = () => {
        const isSafari = navigator.userAgent.toLowerCase().indexOf('safari/') > -1;
        gridApi.setDomLayout(TABLE_LAYOUT_OPTIONS.PRINT);
        setShowPagination(gridApi.gridOptionsWrapper.gridOptions.domLayout !== TABLE_LAYOUT_OPTIONS.PRINT);
        setTimeout(() => {
            /* In case any column width adjustments are needed - as per documentation - pinned columns
                    must be unpinned and all width/height data cleared before doing it */
            if (isSafari) {
                // Safari print
                document.execCommand('print', false, null);
            } else {
                // eslint-disable-next-line no-restricted-globals
                print();
            }
            gridApi.setDomLayout(TABLE_LAYOUT_OPTIONS.AUTO_HEIGHT);
            setShowPagination(true);
        }, 500);
    };

    const handleSearchInputChange = searchInput => {
        onSearchInputChange(searchInput);
        if (gridApi) {
            gridApi.setQuickFilter(searchInput); // Refresh is needed for filtered cells highlighting to work properly
            gridApi.refreshCells({
                force: true
            });
            const displayedRowsCount = gridApi.getDisplayedRowCount();
            setTotalRows(displayedRowsCount);
            if (searchInput && displayedRowsCount === 0) {
                gridApi.showNoRowsOverlay();
            } else {
                gridApi.hideOverlay();
            }

        }
    };

    const getPageSummaryText = (currentPage, totalPages) => {
        return `Page ${currentPage} of ${totalPages}`;
    };

    const getRowsSummaryText = (currentPage, pageSize, totalRows) => {
        if (totalRows === 0) {
            return `${totalRows}-${totalRows} of ${totalRows}`;
        }
        const rowsFrom = (currentPage - 1) * pageSize + 1;
        const rowsTo = currentPage * pageSize;
        return `${rowsFrom}-${rowsTo > totalRows ? totalRows : rowsTo} of ${totalRows}`;
    };



    const paginationProps = {
        ariaLabel: 'Pagination',
        buttonLabels: {
            goToFirst: 'Go to first',
            goToLast: 'Go to last',
            goToNext: 'Go to next',
            goToPrevious: 'Go to previous'
        },
        className: '',
        dataTestId: `purchase-history-table-pagination`,
        defaultPaginationPageSize: DEFAULT_ROWS_PER_PAGE_OPTIONS[0],
        gridApi,
        hideBorder: false,
        onTabbingIntoTable: handleTabbingFromPagination,
        pageSizeDropdownId: `purchase-history-page-size-dropdown-table`,
        pageSummaryLabelId: `purchase-history-pagination-summary-label-table`,
        pageSummaryText: getPageSummaryText(totalPages === 0 ? 0 : currentPage, totalPages),
        rowsPerPageLabel: 'Rows per page:',
        rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS,
        rowsSummaryText: getRowsSummaryText(currentPage, paginationPageSize(), totalRows),
        showGoToFirstLastPage: true
    };
    const headerProps = {
        className: '',
        dataTestId: `purchase-history-table-header`,
        disableDownloadButton: hasNoRows,
        disablePrintButton: hasNoRows,
        downloadButtonText: 'Download button',
        hideBorder: false,
        hideTableTitle: false,
        id: `purchase-history-table-header`,
        isAlwaysExpanded: false,
        isDownloadable: true,
        isPrintable: true,
        isSearchable: true,
        onDownloadClick: handleDownloadClick,
        onPrintClick: handlePrintClick,
        onSearchInputChange: handleSearchInputChange,
        overflowButtonProps: undefined,
        printButtonText: 'Print button',
        searchInputName: 'purchase-history-search-input',
        searchInputValue: searchInputValue,
        searchPlaceholder: 'Search all columns'
    };

    if (dealerRefundHistoryData && solutionSubscriptionInfo && Object.keys(solutionSubscriptionInfo).length > 0) {

        updatedDealerPurchaseHistoryData = JSON.parse(JSON.stringify(dealerRefundHistoryData));
        updatedDealerPurchaseHistoryData.forEach((item) => {
            item['location'] = entityIdLocationMap[item.customer_org_id]
            item['amount'] = item.amount/100

            Object.keys(solutionSubscriptionInfo).forEach((key) => {
                if (key === item.app_id) {
                    item['app_details'] =
                    {
                        app_image: solutionSubscriptionInfo[key].overview.solutionLogo,
                        app_name: solutionSubscriptionInfo[key].overview.name,
                        app_id: solutionSubscriptionInfo[key].id
                    }
                }
            })
        })
    }


    return <div data-test='componant-refundreport'>
        {dealerRefundHistoryDataLoaded && <TableContainer className={tableClass} >
            <TableHeader {...headerProps} />
            <TableContent data-testid="table-content" ref={tableContentRef}>
                <AgGridReact defaultColDef={{
                    width: 300,
                    sortable: true
                }}
                    frameworkComponents={frameworkComponentsExtended}
                    columnDefs={columns}
                    onGridReady={handleGridReady}
                    rowData={updatedDealerPurchaseHistoryData}
                    onPaginationChanged={handlePaginationChanged}
                    pagination={true}
                    paginationPageSize={paginationPageSize()}
                    paginationAutoPageSize={false}
                    suppressPaginationPanel rowHeight={42} />
            </TableContent>
            {isAgGridReady && showPagination && <TablePagination defaultPaginationPageSize={defaultPaginationPageSize} {...paginationProps} />}
            <TableColumnDndContainer />
        </TableContainer>}
        {!dealerRefundHistoryDataLoaded && !errorInGettingDealerRefundeHistroyData && <Loader style={LOADER_STYLE} label="Generating Report..." />}
        {!dealerRefundHistoryDataLoaded && errorInGettingDealerRefundeHistroyData && <div>
            <h1>Something went wrong.</h1>
            <h2>
                Please contact Fortellis Support at support@fortellis.io
            </h2>
        </div>}
    </div>
} 